import React, { useState, useEffect, useCallback } from 'react';
import { PiggyBank, TrendingUp, ArrowUpRight, Coins, Receipt, Download, Loader2, BarChart3, Users, PieChart, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function Ahorros() {
  const { token } = useAuth();
  
  // Data State
  const [stats, setStats] = useState({
    totalAhorrado: 0,
    interesesGanados: 0,
    ahorradoresActivos: 0,
    tasaPromedio: '0%'
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/transacciones/ahorros/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchStats();
      setLoading(false);
    };
    loadData();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleDownload = async (reportName) => {
    try {
      // Endpoint sugerido para reportes generales de ahorro
      const response = await fetch(`${API_BASE_URL}/reportes/ahorros/generar?tipo=${reportName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert("Error al generar el reporte. El servicio de reportes podría estar en mantenimiento.");
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Error de conexión al intentar descargar el reporte.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-coop-600 animate-spin" />
          <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest text-xs">Generando Dashboard Ejecutivo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-4">
            <div className="bg-slate-900 p-3 rounded-[1.5rem] shadow-2xl shadow-slate-200">
              <PieChart className="w-8 h-8 text-white" />
            </div>
            Dashboard de Ahorros
          </h1>
          <p className="text-slate-500 mt-2 font-medium">Indicadores estratégicos de captación y rendimiento financiero.</p>
        </div>
        
        <div className="flex gap-4">
          <button 
            onClick={() => handleDownload('Dashboard_Consolidado')}
            className="bg-white text-slate-900 border-2 border-slate-100 px-6 py-3 rounded-2xl hover:bg-slate-50 flex items-center gap-2 font-black transition-all text-sm uppercase tracking-wider"
          >
            <Download className="w-5 h-5" />
            Reporte Anual
          </button>
        </div>
      </div>

      {/* Primary Stats Grid - Large Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-[3rem] p-10 text-white shadow-2xl shadow-indigo-100 relative overflow-hidden group">
          <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4 group-hover:scale-110 transition-transform duration-700">
            <PiggyBank className="w-64 h-64" />
          </div>
          <div className="relative z-10">
            <div className="bg-white/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-8 border border-white/30">
              <Coins className="w-8 h-8 text-white" />
            </div>
            <p className="text-indigo-100 text-xs font-black uppercase tracking-[0.3em] mb-4">Total Cartera de Ahorros</p>
            <h2 className="text-6xl font-black mb-4 tracking-tighter">{formatCurrency(stats.totalAhorrado)}</h2>
            <div className="flex items-center gap-2 text-indigo-200 font-bold">
              <TrendingUp className="w-5 h-5" />
              <span>+12.4% respecto al mes anterior</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
           <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col justify-between">
              <div>
                <div className="bg-emerald-50 w-12 h-12 rounded-xl flex items-center justify-center mb-6 text-emerald-600">
                  <Receipt className="w-6 h-6" />
                </div>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2">Intereses Devengados</p>
                <h3 className="text-3xl font-black text-slate-900">{formatCurrency(stats.interesesGanados)}</h3>
              </div>
              <p className="text-slate-400 text-xs font-medium mt-4 italic">Costo de fondeo proyectado</p>
           </div>

           <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col justify-between">
              <div>
                <div className="bg-amber-50 w-12 h-12 rounded-xl flex items-center justify-center mb-6 text-amber-600">
                  <Users className="w-6 h-6" />
                </div>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2">Ahorradores Activos</p>
                <h3 className="text-3xl font-black text-slate-900">{stats.ahorradoresActivos}</h3>
              </div>
              <div className="flex gap-1 mt-4">
                 {[1,2,3,4,5].map(i => <div key={i} className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white -ml-2 first:ml-0" />)}
                 <span className="text-xs text-slate-400 font-bold ml-2">Socios</span>
              </div>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Market Analysis */}
        <div className="lg:col-span-2 bg-slate-50 rounded-[3rem] p-10 border border-slate-100">
           <div className="flex justify-between items-center mb-10">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">Análisis Comparativo</h2>
              <div className="flex gap-2">
                 <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                 <div className="w-3 h-3 rounded-full bg-slate-200"></div>
              </div>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { label: 'Tasa Pasiva', valor: stats.tasaPromedio, desc: 'Rendimiento anual pagado', icon: TrendingUp },
                { label: 'Eficiencia', valor: '94.2%', desc: 'Uso de fondos captados', icon: BarChart3 },
                { label: 'Seguridad', valor: 'AAA', desc: 'Calificación de riesgo', icon: ShieldCheck }
              ].map((item, i) => (
                <div key={i} className="space-y-4">
                   <div className="flex items-center gap-3 text-slate-400">
                      <item.icon className="w-5 h-5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{item.label}</span>
                   </div>
                   <p className="text-4xl font-black text-slate-900 tracking-tighter">{item.valor}</p>
                   <p className="text-xs text-slate-400 font-medium leading-relaxed">{item.desc}</p>
                </div>
              ))}
           </div>

           <div className="mt-12 p-8 bg-white rounded-3xl border border-slate-200/50 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                 <h4 className="font-black text-slate-900 mb-2">Proyección de Crecimiento</h4>
                 <p className="text-sm text-slate-500 leading-relaxed font-medium">Basado en el comportamiento actual, se espera que la cartera alcance los <span className="text-indigo-600 font-bold">12,500 millones</span> para el cierre del ejercicio.</p>
              </div>
              <button 
                onClick={() => handleDownload('Simulacion_Crecimiento')}
                className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-600 transition-colors shadow-lg"
              >
                 Simular Escenario
              </button>
           </div>
        </div>

        {/* Audit & Compliance Sidebar */}
        <div className="space-y-8">
           <div className="bg-white rounded-[2.5rem] border border-slate-100 p-10 shadow-sm">
              <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
                Reportes PDF
              </h3>
              <div className="space-y-4">
                {[
                  { name: 'Balance General de Ahorros', size: '1.2 MB' },
                  { name: 'Concentración por Plazos', size: '0.8 MB' },
                  { name: 'Informe de Tasas Pasivas', size: '1.5 MB' }
                ].map((doc, i) => (
                  <div 
                    key={i} 
                    onClick={() => handleDownload(doc.name)}
                    className="p-4 bg-slate-50 rounded-2xl hover:bg-indigo-50 transition-colors cursor-pointer group flex items-center justify-between"
                  >
                     <div>
                        <p className="text-xs font-black text-slate-700 uppercase tracking-tight">{doc.name}</p>
                        <p className="text-[10px] text-slate-400 font-bold">{doc.size}</p>
                     </div>
                     <Download className="w-4 h-4 text-slate-300 group-hover:text-indigo-600 transition-colors" />
                  </div>
                ))}
              </div>
           </div>

           <div className="bg-emerald-600 rounded-[2.5rem] p-10 text-white shadow-xl shadow-emerald-100 relative overflow-hidden group">
              <div className="absolute right-0 top-0 p-8 opacity-20 group-hover:rotate-12 transition-transform duration-500">
                <ShieldCheck className="w-20 h-20" />
              </div>
              <h3 className="text-lg font-black mb-4">Estado de Auditoría</h3>
              <p className="text-emerald-100 text-sm font-medium mb-8 leading-relaxed italic">
                "Todas las cuentas de ahorro están conciliadas con el Libro Mayor al cierre de hoy."
              </p>
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest bg-white/20 w-fit px-4 py-2 rounded-full border border-white/30">
                 <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                 Sistema Sincronizado
              </div>
           </div>
        </div>
      </div>
    </div>
  );
}
